import { useEffect, useRef, useState } from "react";
import { loadTapPaySdk, TAPPAY_SERVER_TYPE } from "@utils/tappaySdk";

const APP_ID = Number(process.env.REACT_APP_TAPPAY_APP_ID) || 0;
const APP_KEY = process.env.REACT_APP_TAPPAY_APP_KEY || "";
const APPLE_PAY_MERCHANT_ID = process.env.REACT_APP_APPLE_PAY_MERCHANT_ID || "";

export type ApplePayState = "loading" | "unavailable" | "ready" | "paying";

/**
 * Apple Pay（TapPay paymentRequestApi）狀態機。
 * enabled=false 或金額非正整數 → 尚未偵測，state 停在 loading（不是 unavailable——
 * 呼叫端資料還沒到齊，不代表裝置真的不支援）；
 * 否則載 TapPay SDK → setupSDK（若尚未 setup 過）→ checkAvailability → setupApplePay → setupPaymentRequest，
 * 依可否用已綁卡完成付款回 ready / unavailable（這時才是真的偵測完畢）。
 * amount / label 改變時重新 setupPaymentRequest。
 */
export function useApplePay(opts: { amount: number | null; label: string; enabled: boolean }): {
  state: ApplePayState;
  /** 不可用原因（dev 除錯用） */
  reason: string | null;
  pay: () => Promise<{ ok: true; prime: string } | { ok: false; msg: string }>;
} {
  const { amount, label, enabled } = opts;
  const [state, setState] = useState<ApplePayState>("loading");
  // 不可用的原因（僅供 dev 畫面顯示與除錯；production 不顯示）
  const [reason, setReason] = useState<string | null>(null);
  // pay() 內要判斷「目前是否 ready」，用 ref 避免 closure 抓到舊值。
  const stateRef = useRef<ApplePayState>(state);
  stateRef.current = state;

  const isValidAmount = typeof amount === "number" && Number.isInteger(amount) && amount > 0;

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      // 尚未偵測（呼叫端還沒把 enabled 轉真）≠ 真的不支援；若直接回 unavailable，
      // 呼叫端（例如 PayLinkView）會在還沒偵測完之前閃一下「此裝置不支援 Apple Pay」的錯誤文案。
      setState("loading");
      return;
    }

    if (!isValidAmount) {
      // enabled 已經是 true，代表呼叫端認為資料已到齊、可以開始偵測；金額仍非正整數
      // 不是「還沒載入」而是資料本身有問題（例如後端回傳 0 或非整數），停在 loading
      // 只會讓使用者卡住看不到任何提示，這裡改回可辨識的 unavailable。
      setReason(`金額無效：${String(amount)}`);
      setState("unavailable");
      return;
    }

    setReason(null);
    setState("loading");

    loadTapPaySdk()
      .then(() => {
        if (cancelled) return;

        if (!window.__tappaySdkSetup) {
          window.TPDirect.setupSDK(APP_ID, APP_KEY, TAPPAY_SERVER_TYPE);
          window.__tappaySdkSetup = true;
        }

        const availability = window.TPDirect.paymentRequestApi.checkAvailability();
        if (!availability) {
          if (!cancelled) {
            setReason("TPDirect.paymentRequestApi.checkAvailability() 回 false");
            setState("unavailable");
          }
          return;
        }
        if (!APPLE_PAY_MERCHANT_ID) {
          if (!cancelled) {
            setReason("REACT_APP_APPLE_PAY_MERCHANT_ID 未設定");
            setState("unavailable");
          }
          return;
        }

        window.TPDirect.paymentRequestApi.setupApplePay({
          merchantIdentifier: APPLE_PAY_MERCHANT_ID,
          countryCode: "TW",
        });

        window.TPDirect.paymentRequestApi.setupPaymentRequest(
          {
            supportedNetworks: ["VISA", "MASTERCARD", "JCB"],
            supportedMethods: ["apple_pay"],
            displayItems: [{ label, amount: { currency: "TWD", value: String(amount) } }],
            total: { label, amount: { currency: "TWD", value: String(amount) } },
            options: {
              requestPayerEmail: false,
              requestPayerName: false,
              requestPayerPhone: false,
              requestShipping: false,
            },
          },
          async (r: any) => {
            if (cancelled) return;
            if (r?.canMakePaymentWithActiveCard) {
              setState("ready");
            } else {
              // TapPay 判定不可用時，額外直打原生 ApplePaySession API（不經 TapPay）交叉比對，
              // 協助分辨是「錢包無卡」還是「merchant/網域未驗證」等原生層問題（dev 除錯用）。
              const aps = (window as any).ApplePaySession;
              let native = "ApplePaySession 不存在";
              if (aps) {
                const basic = aps.canMakePayments();
                try {
                  const withCard = await aps.canMakePaymentsWithActiveCard(APPLE_PAY_MERCHANT_ID);
                  native = `canMakePayments=${basic} withActiveCard(${APPLE_PAY_MERCHANT_ID})=${withCard}`;
                } catch (e) {
                  native = `canMakePayments=${basic} withActiveCard threw: ${e instanceof Error ? e.message : String(e)}`;
                }
              }
              if (cancelled) return;
              setReason(
                `setupPaymentRequest：browserSupportPaymentRequest=${String(r?.browserSupportPaymentRequest)} canMakePaymentWithActiveCard=${String(r?.canMakePaymentWithActiveCard)}（錢包內沒有可用卡，或 merchant/網域未驗證）｜native: ${native}`,
              );
              setState("unavailable");
            }
          },
        );
      })
      .catch((err) => {
        console.warn("Apple Pay 初始化失敗", err);
        if (!cancelled) {
          setReason(`初始化失敗：${err instanceof Error ? err.message : String(err)}`);
          setState("unavailable");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, isValidAmount, amount, label]);

  const pay = async (): Promise<{ ok: true; prime: string } | { ok: false; msg: string }> => {
    if (stateRef.current !== "ready") {
      return { ok: false, msg: "Apple Pay 尚未就緒" };
    }

    setState("paying");

    try {
      const result = await new Promise<any>((resolve) => {
        window.TPDirect.paymentRequestApi.getPrime((r: any) => resolve(r));
      });

      if (result?.status === 0 && result?.prime) {
        return { ok: true, prime: result.prime };
      }
      return { ok: false, msg: result?.msg || "取得付款授權失敗" };
    } finally {
      setState("ready");
    }
  };

  return { state, reason, pay };
}
